import { $convertToMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  LexicalCommand,
} from 'lexical';
import { useEffect, useMemo, useRef, useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useInlineDocumentEdit } from 'src/suitability-report/hooks/useInlineDocumentEdit';
import { createTransformers } from '../config/transformers';
import {
  $insertReplacementMarkers,
  $removeReplacementMarkers,
  $setEditorValue,
  normalizeInlineSuggestions,
} from '../utils';
import { findScrollContainer } from '../utils/findScrollContainer';

type SiestaRequestPayload = {
  promptText: string;
  toolbarTopPercent?: number;
};

export const SIESTA_SELECTION_REQUEST_COMMAND: LexicalCommand<SiestaRequestPayload> = createCommand(
  'SIESTA_SELECTION_REQUEST',
);

type Props = {
  documentId?: string;
  clientId?: string;
  onProcessingComplete?: () => void;
  onInlineEditStart?: (top: number) => void;
  onInlineEditComplete?: () => void;
};

export function SiestaInlinePlugin({
  documentId,
  clientId,
  onProcessingComplete,
  onInlineEditStart,
  onInlineEditComplete,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const { inlineEdit } = useInlineDocumentEdit(documentId);
  const transformers = useMemo(() => createTransformers(editor), [editor]);
  const onInlineEditStartRef = useRef(onInlineEditStart);
  onInlineEditStartRef.current = onInlineEditStart;
  const onInlineEditCompleteRef = useRef(onInlineEditComplete);
  onInlineEditCompleteRef.current = onInlineEditComplete;

  const [pendingRequest, setPendingRequest] = useState<{
    markdown: string;
    promptText: string;
  } | null>(null);

  useEffect(() => {
    if (!pendingRequest || !documentId || !clientId) return;

    const { markdown, promptText } = pendingRequest;

    inlineEdit({
      documentContent: markdown,
      editRequest: promptText,
      clientId,
    })
      .then(replacementMarkdown => {
        const normalizedMarkdown = normalizeInlineSuggestions(replacementMarkdown);
        const scrollContainer = findScrollContainer(editor.getRootElement());
        const scrollTop = scrollContainer.scrollTop;

        editor.update(
          () => {
            $setEditorValue(normalizedMarkdown, transformers);
          },
          { tag: 'siesta-replacement' },
        );

        requestAnimationFrame(() => {
          scrollContainer.scrollTop = scrollTop;
        });

        setPendingRequest(null);
        onProcessingComplete?.();
        onInlineEditCompleteRef.current?.();
      })
      .catch(error => {
        notifyAndLogError('Siesta AI Error', 'Failed to transform text. Please try again.', error, {
          prompt: promptText,
          markdown,
        });
        setPendingRequest(null);
        onProcessingComplete?.();
        onInlineEditCompleteRef.current?.();
      });
  }, [
    pendingRequest,
    editor,
    transformers,
    documentId,
    clientId,
    inlineEdit,
    onProcessingComplete,
  ]);

  useEffect(() => {
    return editor.registerCommand(
      SIESTA_SELECTION_REQUEST_COMMAND,
      (payload: SiestaRequestPayload) => {
        const { promptText, toolbarTopPercent } = payload;

        editor.update(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return;
          }

          const markers = $insertReplacementMarkers();
          if (!markers) return;

          const root = $getRoot();
          const markdown = $convertToMarkdownString(transformers, root);

          $removeReplacementMarkers(markers);

          if (!markdown.trim()) {
            return;
          }

          $setSelection(null);

          onInlineEditStartRef.current?.(toolbarTopPercent ?? 50);
          setPendingRequest({ markdown, promptText });
        });

        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, transformers]);

  return null;
}
